import type { YearBucket } from "@/lib/setup/preview";
import { cn } from "@/lib/utils";

const THUMB = cn(
  "pointer-events-none absolute inset-x-0 top-1/2 h-0 w-full -translate-y-1/2 appearance-none bg-transparent",
  "[&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:size-4 [&::-webkit-slider-thumb]:appearance-none",
  "[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary",
  "[&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:size-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary",
);

/** Histogram + two-thumb range. A thumb at either end means "no bound" (undefined). */
export function YearRange({ buckets, from, to, onChange }: {
  buckets: YearBucket[];
  from: number | undefined;
  to: number | undefined;
  onChange: (from: number | undefined, to: number | undefined) => void;
}) {
  const now = new Date().getFullYear();
  // Keep a set bound one step in from the edge: an edge thumb means "no bound".
  const min = Math.min(buckets[0]?.from ?? 1990, from != null ? from - 1 : Infinity);
  const max = Math.max(now, to != null ? to + 1 : -Infinity);
  const lo = from ?? min;
  const hi = to ?? max;
  // Both inputs span the full track: a From thumb pushed right sits under To, so
  // lift it once it is in the upper half (To stays grabbable from the left half).
  const fromOnTop = lo > (min + max) / 2;
  const peak = Math.max(1, ...buckets.map((b) => b.count));
  return (
    <div className="flex flex-col gap-1">
      <div className="flex h-16 items-end gap-px" aria-hidden>
        {buckets.map((b) => (
          <div key={b.from} className={cn("flex-1 rounded-t-sm", b.inRange ? "bg-primary/70" : "bg-muted-foreground/20")}
            style={{ height: `${b.count ? Math.max(6, (b.count / peak) * 100) : 0}%` }} />
        ))}
      </div>
      <div className="relative h-5">
        <div aria-hidden className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded bg-muted" />
        <input type="range" aria-label="From year" min={min} max={max} value={lo} className={cn(THUMB, fromOnTop ? "z-20" : "z-10")}
          onChange={(e) => { const v = Math.min(Number(e.target.value), hi); onChange(v <= min ? undefined : v, to); }} />
        <input type="range" aria-label="To year" min={min} max={max} value={hi} className={cn(THUMB, "z-10")}
          onChange={(e) => { const v = Math.max(Number(e.target.value), lo); onChange(from, v >= max ? undefined : v); }} />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground"><span>{min}</span><span>{max}</span></div>
    </div>
  );
}
